'use client'

import { useEffect, useState } from 'react'
import { reportError } from '@/lib/error-reporter'
import { shouldAutoReload, reloadForChunkError } from '@/lib/chunk-reload'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'

export default function GlobalError({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  // A failed code-chunk download reloads once instead of crashing; not reported unless it fails again.
  const [autoReload] = useState(() => shouldAutoReload(error))

  useEffect(() => {
    if (autoReload) {
      reloadForChunkError()

      return
    }

    reportError({
      severity: 'error',
      category: 'ui_crash',
      message: `[GlobalErrorBoundary] ${error.message}`,
      stackTrace: error.stack?.slice(0, 4000),
      errorCode: error.digest,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
    })
  }, [error, autoReload])

  if (autoReload) return null

  return (
    <Box sx={{ p: 4, maxWidth: 700, mx: 'auto', mt: 8 }}>
      <Typography variant='h5' color='error' gutterBottom>Something went wrong</Typography>
      <Typography variant='body2' color='text.secondary' gutterBottom>
        This error has been automatically reported. Try refreshing or go back.
      </Typography>
      <Typography variant='body2' sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', fontSize: 11, my: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
        {error.message}
      </Typography>
      <Button variant='contained' onClick={reset} sx={{ mr: 1 }}>Try again</Button>
      <Button variant='outlined' onClick={() => window.location.href = '/'}>Go home</Button>
    </Box>
  )
}
